import React, { useState } from 'react'
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'
import { Link } from '@tanstack/react-router'
import { MAX_FILE_SIZE_MB } from '../../../utils/helpers'
import SignUpPromptDialog from '../../../components/SignUpPromptDialog'
import { isSignedIn } from '../../../auth'
import { trackSignUpDialogTriggered } from '../../../utils/analytics'
import { fillTemplate, useHomeStrings } from '../../../i18n'

export default function FileUpload() {
  const t = useHomeStrings().upload
  const [file, setFile] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [compressing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [showRateLimitError, setShowRateLimitError] = useState(false)
  const [showSignUpDialog, setShowSignUpDialog] = useState(false)
  const [dialogReason, setDialogReason] = useState('file-size')
  const signedIn = isSignedIn()

  const handleSingleFile = (selectedFile) => {
    setShowRateLimitError(false)

    if (!selectedFile) {
      setFile(null)
      setErrorMessage('')
      return
    }

    setFile(selectedFile)
    const fileSizeMB = selectedFile.size / 1024 / 1024
    
    // Always show sign-up dialog for anonymous users, regardless of file size
    trackSignUpDialogTriggered('file-size', fileSizeMB)
    setDialogReason('file-size')
    setShowSignUpDialog(true)
    setErrorMessage('')
  }

  const handleFileChange = (event) => {
    if (!event.target.files || event.target.files.length === 0) {
      handleSingleFile(null)
      return
    }
    handleSingleFile(event.target.files[0])
  }

  const handleDragOver = (event) => {
    event.preventDefault() 
    event.stopPropagation()
  }

  const handleDragEnter = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const droppedFiles = event.dataTransfer.files
    if (droppedFiles && droppedFiles.length > 0) {
      handleSingleFile(droppedFiles[0])
      event.dataTransfer.clearData()
    }
  }

  const getUploadState = () => {
    if (showRateLimitError) return 'error'
    if (!file) return 'idle'
    // Always show idle state for anonymous users since they can't actually upload
    return 'idle'
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    // Always show sign-up dialog for anonymous users regardless of file size or rate limits
    if (file) {
      const fileSizeMB = file.size / 1024 / 1024
      trackSignUpDialogTriggered('file-size', fileSizeMB)
      setDialogReason('file-size')
      setShowSignUpDialog(true)
    }
  }

  const uploadState = getUploadState()
  const isError = uploadState === 'error'
  const isValid = uploadState === 'valid'

  const borderClass = isError
    ? 'border-danger'
    : isValid
    ? 'border-success'
    : 'border-border'

  const dropAreaClass = isDragging ? 'bg-background-elevated' : ''

  // If user is signed in, show CTA to dashboard instead of upload
  if (signedIn) {
    return (
      <div className="bg-background" id="file-upload">
        <div className="max-w-5xl w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-accent font-semibold tracking-wide uppercase">
              {t.eyebrow}
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
              {t.heading}
            </p>
            <p className="mt-4 max-w-2xl text-xl text-muted-foreground/90 lg:mx-auto">
              {t.signedInLead}
            </p>
          </div>

          <div className="mt-10">
            <div className="max-w-2xl mx-auto bg-background-elevated border border-border/60 rounded-2xl p-12 text-center shadow-sm">
              <div className="space-y-6">
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <ArrowUpTrayIcon className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {t.signedInTitle}
                  </h3>
                  <p className="mt-3 text-base text-muted-foreground/90">
                    {t.signedInText}
                  </p>
                </div>
                <div className="pt-2">
                  <Link
                    to="/app/dashboard"
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-semibold rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent/50 transition-all duration-200 shadow-sm"
                  >
                    {t.signedInButton}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
  <div className="bg-background" id="file-upload">
      <div className="max-w-5xl w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-accent font-semibold tracking-wide uppercase">
            {t.eyebrow}
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
            {t.heading}
          </p>
          <p className="mt-4 max-w-2xl text-xl text-muted-foreground/90 lg:mx-auto">
            {t.lead}
          </p>
        </div>

        <div className="mt-10">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-8">
              <div className="mt-6 space-y-6">
                <div>
                  <label
                    htmlFor="file-upload"
                    className="block text-sm font-medium text-muted-foreground mb-2"
                  >
                    {t.label}
                  </label>
                  <div
                    className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-xl transition-all duration-200 cursor-pointer ${borderClass} ${dropAreaClass} bg-background-elevated hover:border-border`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onClick={() => document.getElementById('file-upload-input').click()}
                  >
                    <div className="space-y-1 text-center">
                      {!file && <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-muted-foreground/70" />}
                      {isValid && <CheckCircleIcon className="mx-auto h-12 w-12 text-success" />}
                      {isError && (
                        <ExclamationCircleIcon className="mx-auto h-12 w-12 text-danger" />
                      )}
                      <div className="flex text-sm text-muted-foreground justify-center">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-background-elevated rounded-lg font-medium text-accent hover:text-accent/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-accent/50 transition-all duration-200"
                        >
                          <span>{t.choose}</span>
                          <input
                            id="file-upload-input"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            onChange={handleFileChange}
                            accept=".glb,.gltf"
                          />
                        </label>
                        <p className="pl-1">{t.dragDrop}</p>
                      </div>
                      <p className="text-xs text-muted-foreground/80">
                        {fillTemplate(t.limit, { size: MAX_FILE_SIZE_MB })}
                      </p>
                      {errorMessage && (
                        <p
                          className={`mt-2 text-sm ${
                            isError ? 'text-danger' : 'text-success'
                          }`}
                        >
                          {errorMessage}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                className="inline-flex justify-center py-3 px-6 border border-transparent text-sm font-semibold rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent/50 transition-all duration-200 disabled:opacity-50 shadow-sm"
                disabled={!file || compressing || showRateLimitError}
              >
                {compressing 
                  ? t.compressing
                  : t.button}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Sign-up prompt dialog */}
      <SignUpPromptDialog
        isOpen={showSignUpDialog}
        onClose={() => setShowSignUpDialog(false)}
        fileName={file?.name || ''}
        fileSize={file ? (file.size / 1024 / 1024) : 0}
        reason={dialogReason}
      />
    </div>
  )
}
